/**
 * CommandK — legacy entry point that Stage 2 shipped as a stub, now
 * a thin compatibility shell over Stage 6's real palette + shortcut
 * registry.
 *
 * Kept so existing call sites (`Sidebar` → "Search origins…",
 * anywhere that imported `useCommandPalette` from this module)
 * continue to compile without edits.
 *
 * What's new in Stage 6:
 *   - `useCommandPalette()` now proxies the `command-palette` store
 *     at `lib/stores/command-palette`.
 *   - The ⌘K binding is registered through the shortcut registry
 *     (inside `_app.tsx`) — this file no longer attaches its own
 *     keydown listener for K.
 *   - ⌘J (AI pane toggle) is still wired here because it's a shell-
 *     local concern, not a palette concern, and removing it would
 *     require threading the AI pane state through the registry.
 */

import { useShell } from '@/components/shell/ShellContext';
import { useEffect } from 'react';

// Re-export the real hook from the store module so consumers that
// `import { useCommandPalette } from '@/components/shell/CommandK'`
// keep working.
export { useCommandPalette } from '@/lib/stores/command-palette';

export function CommandK(): null {
  const { aiOpen, setAiOpen } = useShell();

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const isMod = e.metaKey || e.ctrlKey;
      if (!isMod) return;
      if (e.key === 'j' || e.key === 'J') {
        e.preventDefault();
        setAiOpen(!aiOpen);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [aiOpen, setAiOpen]);

  return null;
}
